import {OrderStatus, StyledButton} from "../../components";
import React from "react";
import {minBy, uniq} from "lodash";
import {OrderStatusPriority, shouldShowPendingWarning} from "../../components/OrderStatusLabel";
import {ActivityLocationTypes, PaymentType} from "../../consts.js";
import moment from "moment";
import {EDIT_ORDER_ERRORS, ORDER_DEADLINE_HOURS, SystemIdToOrderProposalIdPrefix} from "./consts.jsx";
import {StringBuilder} from "../../AppUtils.js";
import {HomeDeliveryUploadMethod} from "../../marketplace/OrderLocationInput/consts";
import {
    getFirstOrderShippingProvider,
    isOrderHasShippingProvider,
    isOrderHasValidDeliveryInfo
} from "../../event/utils";
import {message} from "antd";
import {openWindow} from "../../http/WindowOpener.jsx";
import {NOOP} from "../../utils/NOOP";
import {createProductLink} from "../../marketplace/MarketplaceHelper";
import {generateLionWheelOrderDeliveryStatusLink} from "../../event/OrderDeliveryStatus/utils.jsx";
import {orderBudgetHandlerRequest} from "./columns.jsx";
import {DeliverySystem} from "../AdminDeliveryProviders/consts";
import {RATING_KEYS} from "../../orderFeedback/consts";

export function productBundleOrderStatus(orders) {
    for (const order of orders) {
        if (order.status === OrderStatus.cancelled || order.status === OrderStatus.declined) {
            return order.status;
        }
    }

    const allOrdersStatus = uniq(orders.map(({status}) => status));

    if (allOrdersStatus.length === 1) {
        return allOrdersStatus[0];
    }

    return minBy(orders, order => OrderStatusPriority[order.status]).status;
}

export const validateOrderDisabledForEdit = (order, isAdmin) => {
    if (order?.isVoucherOrder) {
        return {error: EDIT_ORDER_ERRORS.VOUCHER};
    }

    if (isAdmin) {
        return;
    }

    const isOrderFailed = [OrderStatus.cancelled, OrderStatus.declined].includes(order.status);
    if (isOrderFailed) {
        return {error: EDIT_ORDER_ERRORS.CANCEL_OR_DECLINE};
    }

    const editDeadline = moment(order.dtstart).subtract(ORDER_DEADLINE_HOURS, "hours");
    const isEditDeadlinePassed = moment().isAfter(editDeadline);
    if (isEditDeadlinePassed) {
        return {error: EDIT_ORDER_ERRORS.ORDER_CHANGE_TIME_EXPIRED};
    }

    if (order?.paymentType === PaymentType.Card) {
        return {error: EDIT_ORDER_ERRORS.CREDIT_CARD};
    }

    if (order?.needManagerConfirm) {
        return {error: EDIT_ORDER_ERRORS.MANAGER_APPROVE};
    }
};

export const isOrderWaitingForManagerApprove = (order, me) =>
    order?.needManagerConfirm &&
    order.status === OrderStatus.pending &&
    order?.creatingUserId !== me?.userId &&
    (order?.eventStatusMap ?? []).some(({id}) => id === OrderStatus.pendingManagerConfirm);

export const transformOrders = (orders = []) => {
    return orders?.map(order => ({
        ...order,
        proposalId: new StringBuilder(SystemIdToOrderProposalIdPrefix[order?.systemId], "-")
            .append(order?.proposalId)
            .toString()
    }));
};

export const isExcelHomeDelivery = order => {
    return (
        order?.chosenActivityLocation === ActivityLocationTypes.homeDelivery &&
        order?.homeDeliveryUploadMethod === HomeDeliveryUploadMethod.excel
    );
};

export const isSingleHomeDelivery = order => {
    return (
        order?.chosenActivityLocation === ActivityLocationTypes.homeDelivery &&
        order?.homeDeliveryUploadMethod === HomeDeliveryUploadMethod.singleEmployee
    );
};

export const getToastMessageToExcelOrders = (status, event) => {
    if (
        (status === OrderStatus.cancelled || status === OrderStatus.declined) &&
        isOrderHasShippingProvider(event) &&
        isExcelHomeDelivery(event)
    ) {
        message.warn("ההזמנה לא בוטלה עבור חברת השילוח. יש לשלוח את מספר ההזמנה לעדי/רועי.");
    }
};

export const filterOrdersBySearchInput = (orders, searchInput) => {
    if (!searchInput) {
        return orders;
    }

    return orders?.filter(order => {
        const relevantOrders = order?.subOrders?.length ? order.subOrders : [order];

        return relevantOrders.some(
            order =>
                [
                    "companyName",
                    "providerBrand",
                    "proposalId",
                    "productName",
                    "orderName",
                    "companyContactEmail",
                    "companyId",
                    "providerId"
                ].some(key => String(order[key])?.toLowerCase().includes(searchInput.toLowerCase())) ||
                searchInput === order.eventId ||
                order.assignedCSMs?.some(({email}) => email === searchInput.toLowerCase())
        );
    });
};

export const DELIVERY_VIRTUAL_STATUS = {
    DTSTART_ARRIVED_WITHOUT_NO_DRIVER_ASSIGNED: "DTSTART_ARRIVED_WITHOUT_NO_DRIVER_ASSIGNED"
};

export const DELIVERY_VIRTUAL_STATUS_STYLING_TOOLTIP = {
    [DELIVERY_VIRTUAL_STATUS.DTSTART_ARRIVED_WITHOUT_NO_DRIVER_ASSIGNED]: ["#F06060", "אין נהג שמצוות להזמנה זו"]
};

export const getDeliveryVirtualStatus = order => {
    if (!order?.dtstart) {
        return null;
    }

    if (!isOrderHasShippingProvider(order)) {
        return null;
    }

    const dtstartMoment = moment(order.dtstart);
    const untilMoment = moment(order.until ?? order.dtstart);

    const isInDeliveryWindow = moment().isBetween(dtstartMoment, untilMoment, null, "[]");

    if (
        isInDeliveryWindow &&
        order.deliverySystem === DeliverySystem.WellB &&
        !order?.driver &&
        order.status === OrderStatus.accepted &&
        !order.noDriverReviewed
    ) {
        return DELIVERY_VIRTUAL_STATUS.DTSTART_ARRIVED_WITHOUT_NO_DRIVER_ASSIGNED;
    }

    return null;
};

export const DTSTART_VIRTUAL_STATUS = {
    PENDING_DELIVERY_TODAY: "PENDING_DELIVERY_TODAY",
    PENDING_DELIVERY_TOMORROW: "PENDING_DELIVERY_TOMORROW"
};

export const VIRTUAL_STATUS_STYLING = {
    [DTSTART_VIRTUAL_STATUS.PENDING_DELIVERY_TODAY]: ["#FDF0DA", "הזמנה להיום שעדיין לא אושרה", ""],
    [DTSTART_VIRTUAL_STATUS.PENDING_DELIVERY_TOMORROW]: ["#FDF0DA", "הזמנה למחר שעדיין לא אושרה", ""]
};

export const getDtstartVirtualStatus = order => {
    if (!order?.dtstart) {
        return null;
    }

    const dtstartMoment = moment(order.dtstart);
    const createdAtMoment = moment(order.createdAt);
    const isOrderCreatedToday = moment().isSame(createdAtMoment, "day");

    // Check if order delivery date is the same day as creation date (today)
    if (isOrderCreatedToday && dtstartMoment.isSame(createdAtMoment, "day") && order.status === OrderStatus.pending) {
        return DTSTART_VIRTUAL_STATUS.PENDING_DELIVERY_TODAY;
    }

    // Check if order delivery date is tomorrow (as a calendar day)
    const isTomorrow = dtstartMoment.isSame(moment().add(1, "day"), "day");
    if (isOrderCreatedToday && isTomorrow && order.status === OrderStatus.pending) {
        return DTSTART_VIRTUAL_STATUS.PENDING_DELIVERY_TOMORROW;
    }

    return null;
};

export const getDeliveryDriverText = (order, deliveryProviders) => {
    const deliveryProvider = getFirstOrderShippingProvider(order, deliveryProviders);

    return order?.driver
        ? new StringBuilder().append(order?.driver?.name).append("|").append(order?.driver?.phone).toString()
        : new StringBuilder()
              .append("ספק שילוח:")
              .append(deliveryProvider?.name ? `${deliveryProvider.name}` : null)
              .toString();
};

export const generateAdminOrderTableActions = (
    me = {},
    onEdit = NOOP,
    updateStatus = NOOP,
    onHistory = NOOP,
    onImage = NOOP,
    chargeCreditCard = NOOP,
    onSyncOrder = NOOP,
    onChangeDeliveryProvider = NOOP,
    onOpenIssueModal = NOOP,
    enableOrderBudgetHandler = false
) => {
    return [
        {
            title: "פעולות - תפעול הזמנה",
            children: [
                {
                    title: "צפייה בהזמנה",
                    onClick: order => openWindow(`/events/${order.eventId}/orderV2`)
                },
                {
                    title: "אישור עסקה (צד ספק)",
                    onClick: order => updateStatus(order, OrderStatus.accepted),
                    disabled: order => {
                        const isWaitingForManagerApprove = isOrderWaitingForManagerApprove(order, me);
                        return (
                            [OrderStatus.accepted].includes(order.status) ||
                            isWaitingForManagerApprove ||
                            updateStatus === null
                        );
                    },
                    color: (order, isDisabled) => (!isDisabled ? "#00C693" : undefined),
                    shouldHide: updateStatus === NOOP
                },
                {
                    title: "סירוב עסקה (צד ספק)",
                    onClick: order => updateStatus(order, OrderStatus.declined),
                    disabled: order => {
                        const isWaitingForManagerApprove = isOrderWaitingForManagerApprove(order, me);
                        return (
                            [OrderStatus.cancelled, OrderStatus.declined].includes(order.status) ||
                            isWaitingForManagerApprove ||
                            updateStatus === null
                        );
                    },
                    color: (order, isDisabled) => (!isDisabled ? "#F06060" : undefined),
                    shouldHide: updateStatus === NOOP
                },
                {
                    title: "ביטול עסקה (צד אדמין)",
                    onClick: order => updateStatus(order, OrderStatus.cancelled),
                    disabled: order => {
                        const isWaitingForManagerApprove = isOrderWaitingForManagerApprove(order, me);
                        return (
                            [OrderStatus.cancelled].includes(order.status) ||
                            isWaitingForManagerApprove ||
                            updateStatus === null
                        );
                    },
                    shouldHide: updateStatus === NOOP
                },
                {
                    title: "עריכת הזמנה",
                    onClick: order => onEdit(order),
                    disabled: order => {
                        const disableEditOrder = !!validateOrderDisabledForEdit(order, true);
                        return disableEditOrder || onEdit === null;
                    },
                    shouldHide: !me?.features?.edit_order || onEdit === NOOP
                },
                {
                    title: "תיעוד תקלה",
                    onClick: order => onOpenIssueModal(order)
                },
                {
                    title: order => {
                        const isOrderDelivered = order.eventStatusMap?.some(({id}) => id === OrderStatus.delivered);
                        return isOrderDelivered ? "בטלי סימון כסופק" : "סמני כסופק";
                    },
                    onClick: order => {
                        const isOrderDelivered = order.eventStatusMap?.some(({id}) => id === OrderStatus.delivered);
                        updateStatus(order, OrderStatus.delivered, isOrderDelivered);
                    },
                    disabled: order => order.status !== OrderStatus.accepted || updateStatus === null,
                    shouldHide: updateStatus === NOOP
                },
                {
                    title: "חידוש עסקה",
                    onClick: order => updateStatus(order, OrderStatus.pending),
                    disabled: order =>
                        ![OrderStatus.declined, OrderStatus.cancelled].includes(order.status) || updateStatus === null,
                    shouldHide: updateStatus === NOOP
                },

                {
                    title: "חייב אשראי",
                    onClick: order => chargeCreditCard(order.eventId),
                    disabled: order => order.status !== OrderStatus.accepted,
                    danger: true,
                    shouldHide: order => {
                        const isOrderPaid = order.eventStatusMap?.some(({id}) => id === OrderStatus.paid);
                        return !(
                            order.paymentType === PaymentType.Card &&
                            order.status === OrderStatus.accepted &&
                            !isOrderPaid &&
                            moment(order.dtstart).isBefore(moment(), "day")
                        );
                    }
                },
                {
                    title: order => {
                        const isOrderPaid = order.eventStatusMap?.some(({id}) => id === OrderStatus.paid);
                        return isOrderPaid ? "בטל סימון שולם" : "סמן כשולם";
                    },
                    onClick: order => {
                        const isOrderPaid = order.eventStatusMap?.some(({id}) => id === OrderStatus.paid);
                        updateStatus(order, OrderStatus.paid, isOrderPaid);
                    },
                    disabled: updateStatus === null,
                    shouldHide: order => order.paymentType !== PaymentType.Card
                },

                {
                    title: "החזרת כסף ללקוח",
                    onClick: async order => {
                        await orderBudgetHandlerRequest(order, "+");
                    },
                    danger: true,
                    shouldHide: !enableOrderBudgetHandler || !me?.features?.orderbudgethandler
                },
                {
                    title: "לקיחת כסף מהלקוח",
                    onClick: async order => {
                        await orderBudgetHandlerRequest(order, "-");
                    },
                    danger: true,
                    shouldHide: !enableOrderBudgetHandler || !me?.features?.orderbudgethandler
                },
                {
                    title: "צפייה בדירוג הזמנה",
                    onClick: order => openWindow(`/order/${order.eventId}/feedback`),
                    disabled: order => !order?.rating
                }
            ],
            shouldHide: order => !!order?.subOrders?.length
        },
        {
            title: "divider"
        },
        {
            title: "פעולות - שליח",
            children: [
                {
                    title: "צפייה בסטטוס השילוח",
                    onClick: order => openWindow(`/events/${order.eventId}/orderDeliveryStatus`)
                },
                {
                    title: "שינוי חברת שילוחים",
                    onClick: order => onChangeDeliveryProvider(order),
                    shouldHide: !onChangeDeliveryProvider || !me?.features?.change_delivery_shipping_provider
                },
                {
                    title: "צפייה בהזמנה בחברת השילוח",
                    onClick: order => openWindow(generateLionWheelOrderDeliveryStatusLink(order?.shippingDetails)),
                    shouldHide: order => !order.shippingDetails?.public_id
                },
                {
                    title: "סנכרן הזמנה מול חברת המשלוחים",
                    onClick: order => onSyncOrder(order),
                    shouldHide: order =>
                        !(
                            onSyncOrder &&
                            me?.features?.sync_delivery_order &&
                            isOrderHasShippingProvider(order) &&
                            isOrderHasValidDeliveryInfo(order)
                        )
                }
            ]
        },
        {
            title: "divider"
        },
        {
            title: "פעולות - כללי",
            children: [
                {
                    title: "הוספת תמונות או סרטונים",
                    onClick: order => onImage(order),
                    disabled: onImage === null,
                    shouldHide: onImage === NOOP
                },
                {
                    title: "צפייה בהיסטוריית הזמנה",
                    onClick: order => onHistory(order)
                }
            ]
        }
    ];
};

export const generateProviderReminderOperationActions = (
    me = {},
    sendReminderToProvider,
    setDeclineOrder,
    setIssueModalOrder,
    onNoDriverIssue = NOOP,
    onDeclineReview = NOOP,
    onBadRatingIssue = NOOP
) => {
    return [
        {
            shouldShow: order => {
                return shouldShowPendingWarning(order);
            },
            render: order => {
                const isPending = order.status === OrderStatus.pending;
                const isDeclinedOrCancelled = [OrderStatus.cancelled, OrderStatus.declined].includes(order.status);
                const isWaitingForManager = isOrderWaitingForManagerApprove(order, me);

                const canShowDeclineButton = !(isDeclinedOrCancelled || isWaitingForManager);

                return (
                    <div className="admin-orders-operation-cell">
                        <div className="admin-orders-operation-buttons">
                            <StyledButton
                                className="admin-orders-operation-reminder-button"
                                onClick={() => sendReminderToProvider(order)}
                                disabled={!isPending}>
                                תזכורת לספק
                            </StyledButton>
                            {canShowDeclineButton && (
                                <a
                                    className="admin-orders-operation-decline-button"
                                    onClick={() => setDeclineOrder(order)}>
                                    סירוב מהמערכת
                                </a>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            shouldShow: order => (order?.openIssueCount || 0) > 0,
            render: order => {
                return (
                    <div className="admin-orders-operation-cell">
                        <div className="admin-orders-operation-buttons">
                            <StyledButton
                                className="admin-orders-operation-general-button"
                                onClick={() => setIssueModalOrder(order)}>
                                סגירת תקלה
                            </StyledButton>
                        </div>
                    </div>
                );
            }
        },
        {
            shouldShow: order =>
                getDeliveryVirtualStatus(order) === DELIVERY_VIRTUAL_STATUS.DTSTART_ARRIVED_WITHOUT_NO_DRIVER_ASSIGNED,
            render: order => {
                return (
                    <div className="admin-orders-operation-cell">
                        <div className="admin-orders-operation-buttons">
                            <StyledButton
                                className="admin-orders-operation-general-button"
                                onClick={() => onNoDriverIssue(order)}>
                                תיעוד תקלה
                            </StyledButton>
                        </div>
                    </div>
                );
            }
        },
        {
            shouldShow: order => order.status === OrderStatus.declined && !order.declineReviewed,
            render: order => {
                return (
                    <div className="admin-orders-operation-cell">
                        <div className="admin-orders-operation-buttons">
                            <StyledButton
                                className="admin-orders-operation-general-button"
                                onClick={() => onDeclineReview(order)}>
                                שליחת הודעה
                            </StyledButton>
                        </div>
                    </div>
                );
            }
        },
        {
            shouldShow: order =>
                order?.rating?.rating <= RATING_KEYS.BAD && !order?.badRatingReviewed && !order?.openIssueCount,
            render: order => {
                return (
                    <div className="admin-orders-operation-cell">
                        <div className="admin-orders-operation-buttons">
                            <StyledButton
                                className="admin-orders-operation-general-button"
                                onClick={() => onBadRatingIssue(order)}>
                                פתיחת תקלה
                            </StyledButton>
                        </div>
                    </div>
                );
            }
        }
    ];
};
